import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter, usePathname } from "next/navigation";
import { logger } from "@/lib/logger";
import {
  fetchOrganization,
  updateOrganization,
} from "@/services/frontendServices/organizationsServices";
import { UpdateOrganizationSchemaType } from "@/schemas/organizationSchema";
import { OrganizationDto } from "@/types";
import { toast } from "sonner";

// Keys for queries, to maintain consistency
export const organizationKeys = {
  all: ["organizations"] as const,
  lists: () => [...organizationKeys.all, "list"] as const,
  details: () => [...organizationKeys.all, "detail"] as const,
  detail: () => [...organizationKeys.details()] as const,
};

// Hook to get a specific organization by Account ID
export const useOrganization = (options?: { enabled?: boolean }) => {
  return useQuery<OrganizationDto | null, Error>({
    queryKey: organizationKeys.detail(),
    queryFn: () => fetchOrganization(),
    staleTime: 1000 * 60 * 10, // 10 minutes - organization data changes infrequently
    refetchOnWindowFocus: false,
    retry: 3,
    enabled: options?.enabled ?? true,
  });
};

// Hook to update organization data
export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();

  return useMutation({
    mutationFn: (data: UpdateOrganizationSchemaType) =>
      updateOrganization(data),
    onSuccess: (updatedOrganization) => {
      // Get current organization from cache to compare urlSlug
      const currentOrganization = queryClient.getQueryData<OrganizationDto>(
        organizationKeys.detail()
      );

      // Update the organization data in the cache
      queryClient.setQueryData(organizationKeys.detail(), updatedOrganization);

      // Invalidate and refetch organization data to ensure consistency
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(),
      });

      // Check if urlSlug changed (name change triggers slug regeneration)
      if (
        currentOrganization &&
        currentOrganization.urlSlug !== updatedOrganization.urlSlug
      ) {
        // Replace old slug with new slug in current URL
        const newPathname = pathname.replace(
          `/orgs/${currentOrganization.urlSlug}`,
          `/orgs/${updatedOrganization.urlSlug}`
        );
        router.push(newPathname);
      } else {
        // Just refresh to update UI with new data
        router.refresh();
      }
    },
    onError: (error) => {
      logger.error("Error updating organization", error, {
        context: { source: "service", module: "useOrganizations" },
      });
      toast.error(error.message || "Failed to update organization");
    },
  });
};
